import { createView } from '@gonfalon/openapi/operations/createView';
import { deleteView } from '@gonfalon/openapi/operations/deleteView';
import { getLinkedResources } from '@gonfalon/openapi/operations/getLinkedResources';
import { getLinkedViews } from '@gonfalon/openapi/operations/getLinkedViews';
import { getView } from '@gonfalon/openapi/operations/getView';
import { getViews } from '@gonfalon/openapi/operations/getViews';
import { linkResource } from '@gonfalon/openapi/operations/linkResource';
import { unlinkResource } from '@gonfalon/openapi/operations/unlinkResource';
import { updateView } from '@gonfalon/openapi/operations/updateView';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

import { getViewApplicationFlagsQuery } from './viewApplicationFlags';

type ViewCompatibilityPayload = {
  _affectsSdkPayload?: boolean;
  generateSdkKeys?: boolean;
};

function normalizeAffectsSdkPayload<T extends ViewCompatibilityPayload>(view: T): T & { _affectsSdkPayload: boolean } {
  return {
    ...view,
    _affectsSdkPayload: view._affectsSdkPayload ?? view.generateSdkKeys ?? false,
  };
}

export const getViewQuery = createQueryOptions(getView, {
  select: (view) => normalizeAffectsSdkPayload(view),
});
export const getViewsQuery = createQueryOptions(getViews, {
  select: (viewsResponse) => ({
    ...viewsResponse,
    items: viewsResponse.items.map((view) => normalizeAffectsSdkPayload(view)),
  }),
});
export const getLinkedViewsQuery = createQueryOptions(getLinkedViews);
export const getLinkedResourcesQuery = createQueryOptions(getLinkedResources);

export const useCreateView = createMutationHook(createView, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getViewsQuery.partialQueryKey() });
  },
});

export const useUpdateView = createMutationHook(updateView, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getViewsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({ queryKey: getViewQuery.partialQueryKey() });
  },
});

export const useDeleteView = createMutationHook(deleteView, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getViewsQuery.partialQueryKey() });
  },
});

export const useLinkResource = createMutationHook(linkResource, {
  onSuccess: async (_, { projectKey, viewKey }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getViewQuery.partialQueryKey({ projectKey, viewKey }) });
    await queryClient.invalidateQueries({ queryKey: getViewsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getLinkedViewsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getLinkedResourcesQuery.partialQueryKey({ projectKey, viewKey }) });
    // Linking changes each evaluated flag's linked/unlinked status on the
    // Application tab. Not scoped to resourceType 'flags': AI config links
    // cascade to their shadow flags, so other types can change it too.
    await queryClient.invalidateQueries({
      queryKey: getViewApplicationFlagsQuery.partialQueryKey({ projectKey, viewKey }),
    });
  },
});

export const useUnlinkResource = createMutationHook(unlinkResource, {
  onSuccess: async (_, { projectKey, viewKey }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getViewQuery.partialQueryKey({ projectKey, viewKey }) });
    await queryClient.invalidateQueries({ queryKey: getViewsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getLinkedViewsQuery.partialQueryKey({ projectKey }) });
    await queryClient.invalidateQueries({ queryKey: getLinkedResourcesQuery.partialQueryKey({ projectKey, viewKey }) });
    await queryClient.invalidateQueries({
      queryKey: getViewApplicationFlagsQuery.partialQueryKey({ projectKey, viewKey }),
    });
  },
});
